import { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Building2, Pencil, RefreshCw, GitMerge, Search, Layers } from "lucide-react";
import { MergeHotelsDialog } from "../components/MergeHotelsDialog";
import { ManageClustersDialog } from "../components/ManageClustersDialog";
import { toast } from "sonner";

export default function Hotels() {
  const { user } = useAuth();
  const isAdmin = user.role === "corporate_admin";
  const [hotels, setHotels] = useState([]);
  const [clusters, setClusters] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ cluster: "", brand: "", marriott: false });
  const [syncing, setSyncing] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [clustersOpen, setClustersOpen] = useState(false);
  const [search, setSearch] = useState("");

  const syncChpl = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/hotels/sync-chpl");
      toast.success(`CHPL sync complete — ${data.fetched} fetched, ${data.created} added, ${data.updated} updated${data.manual_hidden ? `, ${data.manual_hidden} empty manual hotel${data.manual_hidden === 1 ? "" : "s"} hidden` : ""}`);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSyncing(false);
    }
  };

  const load = () => {
    api.get("/hotels").then((r) => setHotels(r.data));
    api.get("/meta").then((r) => setClusters(r.data.clusters || []));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.put(`/hotels/${editing.id}`, { region: form.cluster, brand: form.brand, marriott: form.marriott });
      toast.success("Hotel updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const q = search.toLowerCase();
  const filtered = hotels.filter((h) => !q || [h.name, h.brand, h.region, h.city, h.state].some((v) => (v || "").toLowerCase().includes(q)));

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="hotels-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-stone-900">Hotels</h1>
          <p className="text-stone-500 mt-1 text-sm">Synced from CHPL Brand Management — assign each hotel to a cluster.</p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-3">
            <Button data-testid="manage-clusters-button" variant="outline" onClick={() => setClustersOpen(true)}>
              <Layers className="w-4 h-4 mr-2" />Clusters
            </Button>
            <Button data-testid="merge-duplicates-button" variant="outline" onClick={() => setMergeOpen(true)}>
              <GitMerge className="w-4 h-4 mr-2" />Merge Duplicates
            </Button>
            <Button data-testid="sync-chpl-button" variant="outline" disabled={syncing} onClick={syncChpl}>
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />{syncing ? "Syncing..." : "Sync from CHPL"}
            </Button>
          </div>
        )}
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <Input data-testid="hotel-search-input" className="pl-9 bg-white" placeholder="Search hotel, cluster, state..." value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      <div className="bg-white border border-[#E7E5DF] rounded-xl overflow-x-auto">
        <table className="w-full min-w-[820px]">
          <thead className="bg-stone-50">
            <tr className="text-left text-[11px] uppercase tracking-wider text-stone-500 border-b border-[#E7E5DF]">
              <th className="py-3 px-4">Hotel</th>
              <th className="py-3 px-4">Brand</th>
              <th className="py-3 px-4">Cluster</th>
              <th className="py-3 px-4">City</th>
              <th className="py-3 px-4">State</th>
              <th className="py-3 px-4">Rooms</th>
              {isAdmin && <th className="py-3 px-4 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody data-testid="hotels-table">
            {filtered.map((h) => (
              <tr key={h.id} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/60">
                <td className="py-3.5 px-4 text-sm font-medium text-stone-800">
                  <span className="flex items-center gap-2"><Building2 className="w-4 h-4 shrink-0 text-[#14532D]" strokeWidth={1.5} />{h.name}
                    {!h.chpl_id && <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-100 text-amber-800 border border-amber-200 shrink-0" data-testid={`hotel-manual-${h.id}`} title="Not from the CHPL API — merge it into its CHPL twin">Not in CHPL</span>}
                  </span>
                </td>
                <td className="py-3.5 px-4 text-sm text-stone-600">{h.brand}</td>
                <td className="py-3.5 px-4 text-sm">
                  {h.region
                    ? <span className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#D1FAE5] text-[#14532D]">{h.region}</span>
                    : <span className="text-[11px] text-amber-600 font-medium" data-testid={`hotel-no-cluster-${h.id}`}>Unassigned</span>}
                </td>
                <td className="py-3.5 px-4 text-sm text-stone-600">{h.city}</td>
                <td className="py-3.5 px-4 text-sm text-stone-600">{h.state}</td>
                <td className="py-3.5 px-4 text-sm text-stone-600">{h.room_count || "—"}</td>
                {isAdmin && (
                  <td className="py-3.5 px-4 text-right">
                    <button data-testid={`hotel-edit-${h.id}`} className="p-1.5 rounded hover:bg-stone-100" title="Assign cluster / edit"
                      onClick={() => { setEditing(h); setForm({ cluster: h.region || "", brand: h.brand || "", marriott: !!h.marriott }); }}>
                      <Pencil className="w-4 h-4 text-stone-500" strokeWidth={1.5} />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="py-12 text-center text-sm text-stone-400">No hotels match.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="hotel-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.name}</DialogTitle>
            <DialogDescription>Hotel details come from CHPL — only the cluster, brand label and Marriott flag are editable here.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Cluster</Label>
              <Input data-testid="hotel-form-cluster" list="cluster-options" value={form.cluster} onChange={(e) => setForm({ ...form, cluster: e.target.value })} placeholder="e.g. West, South, Gujarat..." />
              <datalist id="cluster-options">{clusters.map((c) => <option key={c} value={c} />)}</datalist>
              <p className="text-xs text-stone-400">Pick an existing cluster or type a new name to create one.</p>
            </div>
            <div className="space-y-1.5">
              <Label>Brand</Label>
              <Input data-testid="hotel-form-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
              <input data-testid="hotel-form-marriott" type="checkbox" className="accent-[#14532D] w-4 h-4" checked={form.marriott} onChange={(e) => setForm({ ...form, marriott: e.target.checked })} />
              Marriott affiliated
            </label>
            <Button data-testid="hotel-form-save" onClick={save} className="w-full bg-[#14532D] hover:bg-[#166534]">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      {isAdmin && <MergeHotelsDialog open={mergeOpen} onOpenChange={setMergeOpen} onDone={load} />}
      {isAdmin && <ManageClustersDialog open={clustersOpen} onOpenChange={setClustersOpen} onChanged={load} />}
    </div>
  );
}
